import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ErrorState, ForbiddenState } from '@/components/admin/states';
import { fetchAuditLogs } from '@/features/auditLogs/api';
import { auditActionLabel } from '@/features/auditLogs/labels';
import {
  ATTENDANCE_PART_LABELS,
  ATTENDANCE_STATUS_LABELS,
  type AttendancePart,
  type AttendanceStatus,
} from '@/features/events/attendanceApi';
import {
  buildEventTypeTabs,
  EVENT_SCHEDULE_OPTIONS,
  eventMatchesSchedule,
  EventTypeSection,
  FilterTabRow,
  type EventScheduleFilter,
} from '@/features/events/eventBrowse';
import { fetchMemberAttendance } from '@/features/members/api';
import { fetchEventTypes } from '@/features/masters/api';
import { fetchThreads, threadHeading } from '@/features/messages/api';
import { fetchScoreDetail, fetchScorePeriods } from '@/features/scores/api';
import { fetchSuccessRecords, VISIBILITY_SCOPE_LABELS } from '@/features/success/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/** タブ共通の3状態（読込中/権限不足/失敗）をまとめて返す（指示書 §7-5）. */
function queryStateOr<T>(
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  },
  render: () => T,
): T | ReactElement {
  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) return <ForbiddenState />;
    return <ErrorState onRetry={() => void query.refetch()} />;
  }
  return render();
}

/** 会員詳細「イベント」セクション: 出欠履歴（§7-2 / §12 M12）.

行を押すと、**その受講生のそのイベントでの結果**（出欠・課題の回答・席次など）を
まとめた画面へ移動する（D-150。イベント自体の案内ページではない）。
時期・種別で分ける（D-302・D-303）。0件の種別もタブに出す。
*/
export function MemberEventsTab({ memberId }: { memberId: string }): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [schedule, setSchedule] = useState<EventScheduleFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['member-attendance', memberId],
    queryFn: () => fetchMemberAttendance(memberId),
  });
  // 0件の種別もタブに出すためマスタを使う（受講生は403ならデータ由来にフォールバック）
  const typesQuery = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const scheduleCounts = useMemo(() => {
    const rows = query.data ?? [];
    const now = Date.now();
    let upcoming = 0;
    let past = 0;
    for (const row of rows) {
      if (eventMatchesSchedule(row.starts_at, 'upcoming', now)) upcoming += 1;
      else past += 1;
    }
    return { all: rows.length, upcoming, past };
  }, [query.data]);

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const now = Date.now();
    const bySchedule = rows.filter((row) =>
      eventMatchesSchedule(row.starts_at, schedule, now),
    );
    const q = search.trim().toLowerCase();
    if (!q) return bySchedule;
    return bySchedule.filter(
      (row) =>
        row.event_title.toLowerCase().includes(q) ||
        (row.event_type_name || '').toLowerCase().includes(q),
    );
  }, [query.data, schedule, search]);

  const typeTabs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of filtered) {
      const typeName = row.event_type_name || '（種別なし）';
      counts.set(typeName, (counts.get(typeName) ?? 0) + 1);
    }
    return buildEventTypeTabs(counts, typesQuery.data);
  }, [filtered, typesQuery.data]);

  const visible = useMemo(() => {
    if (typeFilter === null) return filtered;
    return filtered.filter(
      (row) => (row.event_type_name || '（種別なし）') === typeFilter,
    );
  }, [filtered, typeFilter]);

  // 「種別すべて」では件数がある種別だけセクション化。0件はタブの (0) で見せる
  // （空セクションを並べると長くて分かりにくい）。0件タブを押したときだけ空表示。
  const groups = useMemo(() => {
    if (typeFilter !== null) {
      return [{ typeName: typeFilter, rows: visible }];
    }
    const map = new Map<string, typeof visible>();
    for (const row of visible) {
      const typeName = row.event_type_name || '（種別なし）';
      const list = map.get(typeName) ?? [];
      list.push(row);
      map.set(typeName, list);
    }
    return typeTabs
      .filter((t) => (map.get(t.name)?.length ?? 0) > 0)
      .map((t) => ({ typeName: t.name, rows: map.get(t.name)! }));
  }, [visible, typeTabs, typeFilter]);

  useEffect(() => {
    if (typeFilter !== null && !typeTabs.some((t) => t.name === typeFilter)) {
      setTypeFilter(null);
    }
  }, [typeFilter, typeTabs]);

  useEffect(() => {
    setTypeFilter(null);
  }, [schedule]);

  function renderTable(rows: typeof visible): ReactElement {
    return (
      <div className="overflow-x-auto rounded-sm border border-wpborder">
        <table className="w-full border-collapse text-left text-wptable">
          <thead>
            <tr className="border-b border-wpborder bg-wpcanvas/60">
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">イベント</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">開催日時</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">区分</th>
              <th className="px-5 py-3.5 font-semibold text-wptext-strong">出欠</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.event_id}-${row.part}`}
                onClick={() => navigate(`/members/${memberId}/events/${row.event_id}`)}
                className="cursor-pointer border-b border-wpborder last:border-b-0 hover:bg-wpcanvas/60"
              >
                <td className="px-5 py-4 align-middle">
                  <Link
                    to={`/members/${memberId}/events/${row.event_id}`}
                    className="text-wpaccent hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.event_title}
                  </Link>
                </td>
                <td className="px-5 py-4 align-middle">
                  {row.starts_at ? formatDateTime(row.starts_at) : '日程調整中'}
                </td>
                <td className="px-5 py-4 align-middle">
                  {row.attendance_not_required ? (
                    <span className="text-wptext-muted">—</span>
                  ) : (
                    ATTENDANCE_PART_LABELS[row.part as AttendancePart]
                  )}
                </td>
                <td className="px-5 py-4 align-middle">
                  {row.is_meeting || row.attendance_not_required ? (
                    <span className="text-wptext-muted">—</span>
                  ) : row.waitlisted ? (
                    'キャンセル待ち'
                  ) : (
                    ATTENDANCE_STATUS_LABELS[row.status as AttendanceStatus]
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <FilterTabRow
        label="時期"
        active={schedule}
        onChange={(value) => setSchedule(value as EventScheduleFilter)}
        options={EVENT_SCHEDULE_OPTIONS.map((opt) => ({
          value: opt.value,
          label: opt.label,
          count: scheduleCounts[opt.value],
        }))}
      />
      <FilterTabRow
        label="種別"
        active={typeFilter ?? ''}
        onChange={(value) => setTypeFilter(value === '' ? null : value)}
        options={[
          { value: '', label: 'すべて', count: filtered.length },
          ...typeTabs.map((tab) => ({
            value: tab.name,
            label: tab.name,
            count: tab.count,
          })),
        ]}
      />
      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(draftSearch);
        }}
      >
        <label htmlFor={`member-events-search-${memberId}`} className="sr-only">
          イベント名で検索
        </label>
        <input
          id={`member-events-search-${memberId}`}
          type="search"
          value={draftSearch}
          placeholder="イベント名で検索"
          onChange={(event) => setDraftSearch(event.target.value)}
          className="rounded-sm border border-wpborder px-3 py-2 text-wptable"
        />
        <button type="submit" className="btn-secondary">
          検索
        </button>
      </form>
      {queryStateOr(query, () => {
        if (typeFilter !== null && visible.length === 0) {
          return (
            <p className="text-wptext-muted">
              {search.trim()
                ? '検索条件に一致するイベントはありません。'
                : `この時期の${typeFilter}はありません。`}
            </p>
          );
        }
        if (groups.length === 0) {
          return (
            <p className="text-wptext-muted">
              {search.trim()
                ? '検索条件に一致するイベントはありません。'
                : schedule === 'upcoming'
                  ? 'これからのイベントはありません。'
                  : schedule === 'past'
                    ? '終了したイベントはありません。'
                    : '参加イベントはありません。'}
            </p>
          );
        }
        // 種別を1つ選んでいるときは見出しがタブと重複するので表だけ
        if (typeFilter !== null) {
          return renderTable(visible);
        }
        return (
          <div>
            {groups.map((group) => (
              <EventTypeSection
                key={group.typeName}
                title={group.typeName}
                count={group.rows.length}
              >
                {renderTable(group.rows)}
              </EventTypeSection>
            ))}
          </div>
        );
      })}
    </>
  );
}

/** 会員詳細「メッセージ」セクション: 参加スレッドの横断表示（事務局のみ / D-027）. */
export function MemberMessagesTab({ userId }: { userId: string | null }): ReactElement {
  const query = useQuery({
    queryKey: ['member-threads', userId],
    queryFn: () => fetchThreads(null, userId ?? undefined),
    enabled: userId !== null,
  });
  if (userId === null) {
    // 権限の問題ではなく、招待未受諾などでログインアカウントが無い状態（D-149）
    return (
      <p className="text-wptext-muted">
        ログインアカウントが未紐づけのため、メッセージはありません。
      </p>
    );
  }
  return (
    <>
      {queryStateOr(query, () =>
        query.data!.length === 0 ? (
          <p className="text-wptext-muted">参加しているスレッドはありません。</p>
        ) : (
          <ul className="space-y-2">
            {query.data!.map((thread) => (
              <li key={thread.id}>
                <Link to={`/messages/${thread.id}`} className="text-wpaccent hover:underline">
                  {threadHeading(thread)}
                </Link>
                {thread.last_message_at && (
                  <span className="ml-2 text-wptext-muted">
                    最終投稿 {formatDateTime(thread.last_message_at)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

/** 会員詳細「スコア」タブ: 内訳は本人+事務局のみ（D-037）. */
export function MemberScoresTab({ memberId }: { memberId: string }): ReactElement {
  const periodsQuery = useQuery({ queryKey: ['score-periods'], queryFn: fetchScorePeriods });
  const [period, setPeriod] = useState<string | null>(null);
  const selected = period ?? periodsQuery.data?.[0] ?? null;

  const detailQuery = useQuery({
    queryKey: ['member-score', memberId, selected],
    queryFn: () => fetchScoreDetail(selected ?? '', memberId),
    enabled: selected !== null,
    retry: false,
  });

  return (
    <>
      {queryStateOr(periodsQuery, () =>
        periodsQuery.data!.length === 0 ? (
          <p className="text-wptext-muted">公開済みの採点はありません。</p>
        ) : (
          <div>
            <label className="mb-3 block">
              <span className="mb-1.5 block text-wptext-muted">対象月</span>
              <select
                value={selected ?? ''}
                onChange={(e) => setPeriod(e.target.value)}
                className="rounded-sm border border-wpborder px-3 py-2"
              >
                {periodsQuery.data!.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            {detailQuery.isPending ? (
              <p className="text-wptext-muted">読み込み中…</p>
            ) : detailQuery.isError ? (
              detailQuery.error instanceof ApiError && detailQuery.error.status === 403 ? (
                <ForbiddenState message="スコア内訳は本人と事務局のみ閲覧できます。" />
              ) : detailQuery.error instanceof ApiError && detailQuery.error.status === 404 ? (
                <p className="text-wptext-muted">この月のスコアはありません。</p>
              ) : (
                <ErrorState onRetry={() => void detailQuery.refetch()} />
              )
            ) : (
              <div>
                <p className="mb-2">
                  順位 <span className="text-xl font-semibold">{detailQuery.data.rank}位</span>
                  <span className="ml-4">
                    総合点 <span className="text-xl font-semibold">{detailQuery.data.score}</span>
                  </span>
                </p>
                <h4 className="mb-1 font-semibold text-wptext-strong">内訳</h4>
                <ul className="space-y-1">
                  {Object.entries(detailQuery.data.components).map(([key, value]) => (
                    <li key={key} className="text-wptext-muted">
                      {key}: {String(value)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ),
      )}
    </>
  );
}

/** 会員詳細「サクセス記録」タブ: 公開範囲は record 単位で API 側が制御（M10）. */
export function MemberSuccessTab({ memberId }: { memberId: string }): ReactElement {
  const query = useQuery({
    queryKey: ['member-success-records', memberId],
    queryFn: () => fetchSuccessRecords(memberId),
  });
  return (
    <>
      {queryStateOr(query, () =>
        query.data!.length === 0 ? (
          <p className="text-wptext-muted">サクセス記録はありません。</p>
        ) : (
          <ul className="space-y-3">
            {query.data!.map((record) => (
              <li key={record.id} className="rounded-sm border border-wpborder p-3">
                <p className="mb-1 text-wptext-muted">
                  {new Date(record.occurred_at).toLocaleDateString('ja-JP')} /{' '}
                  {VISIBILITY_SCOPE_LABELS[record.visibility_scope]}
                </p>
                <p className="whitespace-pre-wrap">{record.minutes_body}</p>
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

/** 会員詳細「履歴」タブ: 監査ログの対象=この会員で絞り込み（事務局のみ / D-044）. */
export function MemberHistoryTab({ memberId }: { memberId: string }): ReactElement {
  const query = useQuery({
    queryKey: ['member-history', memberId],
    queryFn: () =>
      fetchAuditLogs({ resource_type: 'member', resource_id: memberId, per_page: 50 }),
  });
  return (
    <>
      {queryStateOr(query, () =>
        query.data!.items.length === 0 ? (
          <p className="text-wptext-muted">操作履歴はありません。</p>
        ) : (
          <ul className="space-y-1">
            {query.data!.items.map((log) => (
              <li key={log.id} className="text-wptext-muted">
                {/* 内部コードを画面に出さない（D-083・D-086） */}
                {formatDateTime(log.at)} — {auditActionLabel(log.action)}（
                {log.actor_name ?? '—'}）{log.reason ? ` / 理由: ${log.reason}` : ''}
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}
